// Работа с IndexedDB для кеширования изображений
import { createUrlHash } from '../utils/hash.js';

export class ImageCacheDB {
    constructor() {
        this.dbName = 'ImageCacheDB';
        this.version = 3;
        this.db = null;
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('images')) {
                    const store = db.createObjectStore('images', { keyPath: 'id' });
                    store.createIndex('url', 'url', { unique: false });
                    store.createIndex('postcardId', 'postcardId', { unique: false });
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                }
            };
        });
    }

    async saveImage(url, imageBlob, metadata) {
        if (!this.db) await this.init();
        
        const transaction = this.db.transaction(['images'], 'readwrite');
        const store = transaction.objectStore('images');
        
        return new Promise((resolve, reject) => {
            // Используем estimatedSize из метаданных если blob равен null
            const size = imageBlob ? imageBlob.size : (metadata?.estimatedSize || 0);
            const id = createUrlHash(url);
            
            const request = store.put({
                id,
                url,
                postcardId: metadata?.postcardId || null,
                blob: imageBlob,
                metadata,
                timestamp: Date.now(),
                size: size
            });
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async getImage(url) {
        if (!this.db) await this.init();
        
        const transaction = this.db.transaction(['images'], 'readonly');
        const store = transaction.objectStore('images');
        
        return new Promise((resolve, reject) => {
            const id = createUrlHash(url);
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getAllImages() {
        if (!this.db) await this.init();
        
        const transaction = this.db.transaction(['images'], 'readonly');
        const store = transaction.objectStore('images');
        
        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async clearAll() {
        if (!this.db) await this.init();
        
        const transaction = this.db.transaction(['images'], 'readwrite');
        const store = transaction.objectStore('images');
        
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async getCacheStats() {
        const images = await this.getAllImages();
        const totalSize = images.reduce((sum, img) => {
            // Используем реальный размер из blob или метаданных
            if (img.blob && img.blob.size) {
                return sum + img.blob.size;
            } else if (img.metadata && img.metadata.realSize) {
                return sum + img.metadata.realSize;
            } else {
                return sum + (img.size || 0);
            }
        }, 0);
        
        return {
            count: images.length,
            totalSize: totalSize,
            sizeFormatted: this.formatSize(totalSize)
        };
    }

    formatSize(bytes) {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
}
